import moment from 'moment';
import dateFilter from '@/filters/date';
import { MANAGE_BILLING_GLOSSES, IMPORT_BILLING_STATEMENTS } from '@/data/actions/modules/financial';

export { MANAGE_BILLING_GLOSSES, IMPORT_BILLING_STATEMENTS };

export const GUIDE_TYPES = {
  consultation: 'Consulta',
  sadt: 'SP/SADT',
  simple: 'Simples',
  mixed: 'Consulta + SP/SADT',
};

export const MIXED_GUIDE_TYPE = 'mixed';

export const MIXABLE_GUIDE_TYPES = ['consultation', 'sadt'];

export const PAYMENT_MILESTONES = {
  closed: 'fechamento',
  sent: 'envio',
  protocol: 'protocolo',
  invoice: 'nota fiscal',
};

export const EXPECTED_PAYMENT_SOURCES = {
  calculated: { label: 'Calculada pela regra do plano', icon: 'calculator' },
  manual: { label: 'Informada manualmente', icon: 'pencil' },
  statement: { label: 'Data do demonstrativo de pagamento', icon: 'file-import' },
};

export const ANALYSIS_STATUSES = {
  pending: 'Aguardando análise',
  ok: 'Liberada',
  glossed_partial: 'Glosa parcial',
  glossed_total: 'Glosada',
  in_appeal: 'Em recurso',
  accepted: 'Glosa aceita',
  maintained: 'Glosa mantida',
  recovered: 'Recuperada',
};

export const REQUIRED_TISS_VERSION = '4.03.00';

export const MONTHS = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

export function guideTypeName(type) {
  return GUIDE_TYPES[type] || type || '';
}

export function batchTypeOf(guides) {
  const types = [...new Set(guides.map(guide => guide.type).filter(Boolean))];
  if (types.length === 0) return null;
  return types.length > 1 ? MIXED_GUIDE_TYPE : types[0];
}

export function canMixTypes(type, batchType, allowMixed) {
  if (!batchType || type === batchType) return true;
  if (!allowMixed) return false;
  return MIXABLE_GUIDE_TYPES.includes(type)
    && (batchType === MIXED_GUIDE_TYPE || MIXABLE_GUIDE_TYPES.includes(batchType));
}

export function listDays(days) {
  const list = [...(days || [])].map(Number).sort((a, b) => a - b);
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} e ${list[list.length - 1]}`;
}

function scheduleYears(rule) {
  const years = (rule.schedule || [])
    .map(cycle => (cycle.cutoffDate ? moment(cycle.cutoffDate).year() : null))
    .filter(Boolean);
  return [...new Set(years)].sort().join('/');
}

export function paymentRuleLabel(rule) {
  if (!rule) return '';
  if (rule.type === 'fixedDays') {
    const days = rule.fixedDays && rule.fixedDays.days ? rule.fixedDays.days : [];
    const grace = rule.fixedDays && Number(rule.fixedDays.graceDays);
    if (days.length === 0) return 'Pagamento: dias fixos não configurados';
    const graceLabel = grace ? ` · carência ${grace} dias` : '';
    return `Pagamento: ${days.length === 1 ? 'dia' : 'dias'} ${listDays(days)} do mês${graceLabel}`;
  }
  if (rule.type === 'schedule') {
    const years = scheduleYears(rule);
    return years ? `Pagamento: cronograma ${years}` : 'Pagamento: cronograma sem ciclos';
  }
  const { days, from } = rule.daysAfter || {};
  return `Pagamento: ${days} dias após o ${PAYMENT_MILESTONES[from] || 'envio'}`;
}

function pendingHint(basis) {
  if (basis === 'no_cycle') return 'Nenhum ciclo do cronograma cobre um envio de hoje';
  if (basis === 'no_fixed_days') return 'Regra sem dias fixos configurados no plano';
  const milestone = PAYMENT_MILESTONES[String(basis || '').replace('pending_', '')];
  return milestone ? `Aguardando ${milestone}` : 'Sem previsão';
}

export function forecastHint(rule, forecast) {
  if (!forecast || !forecast.date) return pendingHint(forecast && forecast.basis);
  if (forecast.basis === 'schedule' && forecast.cycle) {
    return `se enviado até ${dateFilter(forecast.cycle.cutoffDate)} → crédito ${dateFilter(forecast.date)}`;
  }
  if (forecast.basis === 'fixedDays' && rule && rule.fixedDays) {
    const grace = Number(rule.fixedDays.graceDays);
    const graceLabel = grace ? `carência de ${grace} dias após o envio` : 'após o envio';
    return `${rule.fixedDays.days.length === 1 ? 'dia' : 'dias'} ${listDays(rule.fixedDays.days)} do mês · ${graceLabel}`;
  }
  const days = rule && rule.daysAfter ? rule.daysAfter.days : null;
  const milestone = PAYMENT_MILESTONES[forecast.basis] || 'envio';
  return `${days ? `${days} dias após o ${milestone}` : `após o ${milestone}`}${forecast.estimated ? ' · estimado' : ''}`;
}

export function expectedPaymentDescription(batch) {
  const expected = (batch && batch.expectedPayment) || {};
  if (expected.source === 'manual') return EXPECTED_PAYMENT_SOURCES.manual.label;
  if (expected.source === 'statement') return EXPECTED_PAYMENT_SOURCES.statement.label;
  if (!batch || !batch.dates || !batch.dates.expectedPaymentAt) return pendingHint(expected.basis);
  if (expected.basis === 'schedule' && expected.cycle) {
    return `Cronograma · corte ${dateFilter(expected.cycle.cutoffDate)}`;
  }
  if (expected.basis === 'fixedDays') return 'Dia fixo do mês da operadora';
  const milestone = PAYMENT_MILESTONES[expected.basis];
  if (!milestone) return EXPECTED_PAYMENT_SOURCES.calculated.label;
  return expected.estimated
    ? `Estimativa · aguardando ${milestone}`
    : `Contada a partir do ${milestone}`;
}

export function analysisStatusName(status) {
  return ANALYSIS_STATUSES[status] || status || '';
}

export function versionDigits(version) {
  const digits = String(version || '').replace(/\D/g, '');
  return digits ? Number(digits) : 0;
}

export function tissOutdated(tiss) {
  return !!(tiss && tiss.enabled && tiss.version
    && versionDigits(tiss.version) < versionDigits(REQUIRED_TISS_VERSION));
}

export const PASSWORD_EXPIRING_DAYS = 3;

export function passwordExpiringDays(guide) {
  const authorization = (guide && guide.authorization) || {};
  if (!authorization.validity || !guide || guide.type === 'consultation') return null;
  const validity = moment(authorization.validity);
  if (!validity.isValid()) return null;
  const days = validity.startOf('day').diff(moment().startOf('day'), 'days');
  return days >= 0 && days <= PASSWORD_EXPIRING_DAYS ? days : null;
}

export function passwordExpiringLabel(guide) {
  const days = passwordExpiringDays(guide);
  if (days === null) return null;
  if (days === 0) return 'Senha vence hoje';
  return `Senha vence em ${days} ${days === 1 ? 'dia' : 'dias'}`;
}

export function guideNumber(guide) {
  if (!guide) return '';
  return (guide.referral && guide.referral.provider) || guide.code || '';
}

export function competenceLabel(competence) {
  const date = moment(competence, 'YYYY-MM', true);
  if (!date.isValid()) return '';
  return `${MONTHS[date.month()]}/${date.year()}`;
}

export function competenceShortLabel(competence) {
  const date = moment(competence, 'YYYY-MM', true);
  if (!date.isValid()) return '';
  const month = MONTHS[date.month()].slice(0, 3);
  return `${month.charAt(0).toUpperCase()}${month.slice(1)}/${date.year()}`;
}

export function competenceParams(competence) {
  const date = moment(competence, 'YYYY-MM', true);
  if (!date.isValid()) return {};
  return { month: date.month() + 1, year: date.year() };
}

export function sumBy(items, getter) {
  return Math.round(items.reduce((sum, item) => sum + (Number(getter(item)) || 0), 0) * 100) / 100;
}

export function planRoute(insuranceId, planId) {
  return { path: `/clinical/insurances/${insuranceId}/plans/${planId}/edit` };
}

export function batchRoute(batchId) {
  return { path: `/financial/billing-batches/${batchId}` };
}

export function financialRoute(financialId) {
  return { path: `/financial/transactions/${financialId}/edit` };
}

export function groupTransfers(guides) {
  const groups = new Map();
  guides.forEach((guide) => {
    (guide.transfers || []).forEach((transfer) => {
      const key = String(transfer.professionalId || transfer.name || 'x');
      const current = groups.get(key) || {
        professionalId: transfer.professionalId,
        name: transfer.name || 'Profissional não identificado',
        guides: 0,
        amount: 0,
      };
      current.guides += 1;
      current.amount = Math.round((current.amount + (Number(transfer.amount) || 0)) * 100) / 100;
      groups.set(key, current);
    });
  });
  return [...groups.values()].sort((a, b) => b.amount - a.amount);
}

export function groupByProfessional(guides) {
  const groups = new Map();
  guides.forEach((guide) => {
    const name = (guide.professional && guide.professional.name) || 'Sem profissional';
    const key = (guide.professional && guide.professional.id) || name;
    const current = groups.get(key) || { name, guides: 0, amount: 0 };
    current.guides += 1;
    const informed = (guide.totals && guide.totals.informed) || 0;
    current.amount = Math.round((current.amount + informed) * 100) / 100;
    groups.set(key, current);
  });
  return [...groups.values()].sort((a, b) => b.amount - a.amount);
}

export function idOf(doc) {
  if (!doc) return null;
  // eslint-disable-next-line no-underscore-dangle
  const value = doc.id || doc._id;
  return value ? String(value) : null;
}
